import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../contexts/AuthContext';
import { apiClient } from '../../services/api';
import { logger } from '../../services/logger';
import type { WhatsNewResponse } from '../../types';
import { afterInitialPaint } from '../../utils/afterInitialPaint';

const SHELL_QUERY_STALE_MS = 30_000;

export function userOrganizationsQueryOptions(tenantId?: string, userId?: string) {
  return {
    queryKey: ['user-organizations', tenantId, userId] as const,
    queryFn: async () => {
      await afterInitialPaint();
      if (!tenantId || !userId) {
        throw new Error('Sign in to load organizations.');
      }

      try {
        return await apiClient.getUserOrganizations();
      } catch (error) {
        logger.warn('Failed to load organizations', { error });
        throw error;
      }
    },
    staleTime: SHELL_QUERY_STALE_MS,
    retry: false as const,
  };
}

export function useUserOrganizations() {
  const { user } = useAuth();
  return useQuery({
    ...userOrganizationsQueryOptions(user?.tenantId, user?.userId),
    enabled: Boolean(user?.tenantId && user?.userId),
  });
}

export function useWhatsNewQuery() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const queryKey = ['whats-new', user?.tenantId, user?.userId] as const;
  const query = useQuery({
    queryKey,
    queryFn: async () => {
      await afterInitialPaint();
      try {
        return await apiClient.getWhatsNew();
      } catch (error) {
        logger.warn('Failed to check release updates', { error });
        throw error;
      }
    },
    enabled: Boolean(user?.tenantId && user?.userId),
    staleTime: SHELL_QUERY_STALE_MS,
    retry: false,
  });

  const dismiss = async () => {
    await queryClient.cancelQueries({ queryKey, exact: true });
    queryClient.setQueryData<WhatsNewResponse>(queryKey, (previous) =>
      previous ? { ...previous, hasUpdates: false } : previous,
    );
    try {
      await apiClient.dismissWhatsNew();
    } catch (error) {
      logger.warn('Failed to dismiss release updates', { error });
      await queryClient.invalidateQueries({ queryKey, exact: true, refetchType: 'none' });
    }
  };

  return { data: query.data, dismiss };
}
